import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { FleetCard } from "../components/fleet/FleetCard";
import { FleetFilters, DEFAULT_FILTERS, applyFilters } from "../components/fleet/FleetFilters";
import { VehicleDetailModal } from "../components/fleet/VehicleDetailModal";
import { useSeo, breadcrumb } from "../lib/seo";
import { BadgeCheck, Sparkles, Route } from "lucide-react";

const FLEET_CATEGORIES = [
  { key: "luxury", title: "Luxury & Premium", desc: "Turn every arrival into a statement. Flagship sedans and prestige SUVs with the finish, presence and comfort that speak for themselves.", match: (l) => /luxury|premium|hot hatch/i.test(l) },
  { key: "family", title: "7-Seaters & People Movers", desc: "Room for the whole crew and the luggage too, made for family road trips, airport runs and group getaways across the Emirates.", match: (l) => /7-seat|mpv|minivan/i.test(l) },
  { key: "adventure", title: "4x4 & Adventure", desc: "Built for the dunes, the mountains and everything between. Confident 4x4s for weekend escapes to Hatta, Jebel Jais and beyond.", match: (l) => /4x4/i.test(l) },
  { key: "suv", title: "Crossovers & SUVs", desc: "The UAE all-rounder, with a commanding view, easy parking and space to spare for city life and coastal drives alike.", match: (l) => /crossover|suv/i.test(l) },
  { key: "sedan", title: "Sedans", desc: "Smooth, economical and effortlessly comfortable, the sweet spot for business trips, daily commutes and long motorway miles.", match: (l) => /sedan/i.test(l) },
  { key: "city", title: "Hatchbacks & City Cars", desc: "Nippy, easy on the wallet and a breeze to park, the smart pick for zipping around town and slotting into that last mall space.", match: () => true },
];
const categoryFor = (label) => FLEET_CATEGORIES.find((c) => c.match(label || "")) || FLEET_CATEGORIES[FLEET_CATEGORIES.length - 1];

const TRUST = [
  { icon: BadgeCheck, title: "Model or Similar Guarantee", text: "Your booking reserves a vehicle category, and you'll receive the displayed model or a comparable vehicle in that group, fully serviced and ready to drive." },
  { icon: Sparkles, title: "Young & Modern UAE Fleet", text: "A regularly refreshed fleet, fully serviced and maintained to high safety standards for confident driving across the Emirates." },
  { icon: Route, title: "Unlimited Mileage Within UAE", text: "Daily and weekly rentals include unlimited mileage within the UAE, with 24/7 roadside assistance included on every car." },
];

export default function Fleet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [galleries, setGalleries] = useState({});
  const [branches, setBranches] = useState(null);
  const [branch, setBranch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/fleet/galleries").then((r) => setGalleries(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const shown = useMemo(() => applyFilters(vehicles, filters), [vehicles, filters]);

  const catCounts = useMemo(() => {
    const counts = {};
    vehicles.forEach((v) => { const c = categoryFor(v.group_label); counts[c.key] = (counts[c.key] || 0) + 1; });
    return counts;
  }, [vehicles]);

  useSeo({
    title: "Car Rental Fleet in Dubai & Northern Emirates | Europcar",
    description: "Browse the Europcar UAE fleet: hatchbacks, sedans, crossovers, SUVs, 7-seaters, 4x4s and luxury models. Unlimited mileage in the UAE, prices incl. VAT.",
    jsonLd: [
      breadcrumb([["Home", "/"], ["Our Fleet", "/fleet"]]),
      {
        "@context": "https://schema.org", "@type": "ItemList",
        name: "Europcar UAE Vehicle Categories",
        itemListElement: FLEET_CATEGORIES.map((c, i) => ({
          "@type": "ListItem", position: i + 1, name: c.title, description: c.desc,
        })),
      },
    ],
  });

  const reserve = (v) => {
    const params = new URLSearchParams({ branch: branch || "DXB-T3" });
    navigate(`/?${params.toString()}#book`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="promo-stripe">
            <h1 className="heading-upper text-2xl sm:text-3xl text-white" data-testid="fleet-heading">{t("Explore our entire vehicle fleet")}</h1>
            <p className="text-white/80 text-sm mt-2 max-w-2xl">{t("From nippy city cars to weekend-ready 4x4s and prestige SUVs, find the one that fits your plans, check the details that matter, then see it's free on your dates.")}</p>
          </div>
          <div className="mt-6 flex flex-wrap items-end gap-4">
            <div className="min-w-[260px]">
              <label className="text-[10px] uppercase tracking-widest text-white/70 block mb-1">{t("Pickup location / branch")}</label>
              <select className="field-input" value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="fleet-branch-select">
                <option value="">{t("All Europcar UAE branches")}</option>
                {branches?.groups.flatMap((g) => g.stations).map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.emirate})</option>
                ))}
              </select>
            </div>
            <div className="text-white">
              <div className="text-3xl font-bold text-[#FFDC00]" data-testid="fleet-count">{shown.length}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/70">{t("Available models · rates incl. VAT · confirm live rate at booking")}</div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full flex-1">
        <div className="mb-8" data-testid="fleet-categories">
          <div className="promo-stripe mb-6">
            <p className="eyebrow mb-1">{t("Something for every journey")}</p>
            <h2 className="heading-upper text-2xl text-[#144733]">{t("Find your perfect match")}</h2>
            <p className="text-sm text-[#57685e] mt-1 max-w-2xl">{t("From wallet-friendly city runabouts to weekend-ready 4x4s and prestige SUVs, pick the group that fits your journey, then choose your exact car below.")}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FLEET_CATEGORIES.filter((c) => catCounts[c.key]).map((c) => (
              <div key={c.key} className="bg-white border border-[#E2E8F0] rounded-2xl p-5 card-lift" data-testid={`fleet-cat-${c.key}`}>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <h3 className="font-bold text-sm text-[#0F1E17]">{t(c.title)}</h3>
                  <span className="text-xs font-semibold text-[#009900] whitespace-nowrap">{catCounts[c.key]} {t("cars")}</span>
                </div>
                <p className="text-xs text-[#57685e] leading-relaxed">{t(c.desc)}</p>
              </div>
            ))}
          </div>
        </div>

        <FleetFilters vehicles={vehicles} filters={filters} setFilters={setFilters} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="fleet-grid">
          {shown.map((v) => (
            <FleetCard key={v.id} v={v} gallery={galleries[v.id]} onReserve={reserve} onDetails={setDetail} />
          ))}
        </div>
        {shown.length === 0 && vehicles.length > 0 && (
          <p className="text-center text-sm text-[#57685e] py-16" data-testid="fleet-no-results">{t("No vehicles match these filters. Try resetting.")}</p>
        )}

        <div className="grid sm:grid-cols-3 gap-4 mt-12" data-testid="fleet-trust-strip">
          {TRUST.map((item) => (
            <div key={item.title} className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
              <item.icon className="w-6 h-6 text-[#009900] mb-2" />
              <h3 className="font-bold text-sm text-[#0F1E17]">{t(item.title)}</h3>
              <p className="text-xs text-[#57685e] mt-1 leading-relaxed">{t(item.text)}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-[#144733] rounded-2xl p-6 sm:p-8 flex flex-wrap items-center justify-between gap-6" data-testid="fleet-addons-cta">
          <div className="max-w-2xl promo-stripe">
            <h3 className="heading-upper text-xl text-white">{t("Make any car yours")}</h3>
            <p className="text-sm text-white/80 mt-2">{t("Zero-excess SCDW from AED 35/day, No-Deposit rental, personal accident insurance, child seats and additional drivers. Every extra is one tap at booking, priced upfront with VAT shown.")}</p>
          </div>
          <Link to="/#book" className="btn-cta rounded px-8 py-3.5 text-sm" data-testid="fleet-book-cta">{t("Check availability & book")}</Link>
        </div>
      </main>
      <VehicleDetailModal vehicle={detail} gallery={detail ? galleries[detail.id] : null} onClose={() => setDetail(null)} />
      <Footer />
    </div>
  );
}
